import { useCallback, useEffect, useState } from "react";
import CommonTable from "../CommonTable";
import PageRoutes from "@/utils/constants";
import { Box, Skeleton, Typography } from "@mui/material";
import useConfig from "@/hooks/useConfig";
import { useTeamContext } from "@/Common/Contex/TeamContext";
import { useNavigate } from "react-router-dom";
import { addImgSizeToUrl, fetchFromApi } from "@/service/api";
const StandingTable = () => {
  const headers: any = [
    { id: "team", label: "Team", align: "left", width: "80%" },
    // { id: "gp", label: "GP", align: "left", width: "20%" },
    // { id: "pts", label: "PTS", align: "left", width: "20%" },
    { id: "record", label: "Record", align: "left", width: "20%" },
  ];
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const config = useConfig();
  const [standings, setStandings] = useState<any[]>([]);
  const { currentSelectedTeam }: any = useTeamContext();
  const navigate = useNavigate();
  // Format standings data for display
  const formattedStandings =
    standings?.map((team: any) => {
      return {
        team: team?.team?.title || "-",
        gp: team?.stats?.GP || 0,
        pts: team?.stats?.PTS || 0,
        record: team?.stats?.GTYPEREC || "-",
        teamData: team?.team || {},

        // Only include specific fields from the original data to avoid object rendering issues
        id: team?.id,
        rank: team?.rank,
        division: team?.division?.title || "-",
        gameType: team?.gameType,
        reason: team?.reason,
        game_id: team?.game_id,
        logoUrl: team?.team?.logoUrl,
      };
    }) || [];

  // Custom cell renderer for formatting cells
  const renderCell = (row: any, columnId: string, index: number) => {
    // Handle team column with logo and name
    if (columnId === "team") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          {row?.logoUrl && (
            <Box
              component="img"
              src={addImgSizeToUrl(row?.logoUrl, 256)}
              alt={row?.team || "Team logo"}
              sx={{
                height: 30,
                width: 30,
                objectFit: "contain",
                mr: 1,
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, fontStyle: "normal", textTransform: "none" }}
          >
            {row?.team || "-"}
          </Typography>
        </Box>
      );
    }

    // For GP column - games played
    if (columnId === "gp") {
      return (
        <Typography
          variant="body2"
          sx={{ fontWeight: 400, fontStyle: "normal", textTransform: "none" }}
        >
          {row?.gp || "0"}
        </Typography>
      );
    }

    // For PTS column - points
    if (columnId === "pts") {
      return (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontStyle: "normal", textTransform: "none" }}
        >
          {row?.pts || "0"}
        </Typography>
      );
    }

    // For Record column
    if (columnId === "record") {
      return (
        <Typography
          variant="body2"
          sx={{
            fontStyle: "normal",
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
        >
          {row?.record || "-"}
        </Typography>
      );
    }

    // Default rendering for other columns
    return row[columnId]?.toString() || "-";
  };

  const GetStanding = useCallback(async () => {
    if (!currentSelectedTeam?.team_id) {
      return;
    }
    try {
      setIsLoading(true);
      const myclub = await fetchFromApi("/myclub", {
        "filter[nid]": `4-${currentSelectedTeam?.team_id_tts}`,
      });
      const seasons = myclub?.data?.seasons;

      const currentSeason = seasons?.find((s: any) => s?.is_active);
      const standings = await fetchFromApi("/standings", {
        "filter[seasons]": currentSeason?.id,
        "filter[division]": "overall",
      });
      const findDivision = standings?.data?.find((d: any) => {
        const divi = d?.divisionId === currentSeason?.divisions[0]?.id;
        return divi;
      });

      setStandings(findDivision?.standings);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching standings:", error);
    }
  }, [currentSelectedTeam]);
  // Function to set sample standings data for testing

  useEffect(() => {
    // Using sample data instead of API data
    // Comment out the line below if you want to use sample data only
    GetStanding();
  }, [GetStanding]);

  // Handle row click
  const handleRowClick = (row: any) => {
    // You can navigate to game details or perform any action when a row is clicked
    console.log("Row clicked:", row);

    // Example: Open game details or show more information
    if (row?.game_id) {
      navigate(PageRoutes.Watch.replace(":gameId", row.game_id));
      // You could navigate to a game details page or open a modal
      // Example: navigate(`/game/${row.game_id}`)
      // Or open a modal with game details
    }
  };
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        <Skeleton variant="rectangular" sx={{ height: 50 }} />
        <Skeleton variant="rectangular" sx={{ height: 50 }} />
        <Skeleton variant="rectangular" sx={{ height: 50 }} />
        <Skeleton variant="rectangular" sx={{ height: 50 }} />
        <Skeleton variant="rectangular" sx={{ height: 50 }} />
        <Skeleton variant="rectangular" sx={{ height: 50 }} />
      </Box>
    );
  }
  return (
    <CommonTable
      headers={headers}
      rows={formattedStandings}
      renderCell={renderCell}
      maxHeight={500}
      title="Standing"
      emptyMessage="No standing data available"
      bodyRowProps={(row: any) => ({
        onClick: () => handleRowClick(row),
        sx: {
          cursor: "pointer",
          "&:hover": {
            backgroundColor: `${
              currentSelectedTeam?.team_secondary_color || config.secondary
            }22`, // Adding transparency
          },
        },
      })}
    />
  );
};
export default StandingTable;
